// Opponents' feet: planted on the floor where they land while the body goes
// where the sim puts it, each foot stepping as the body moves on past it, to
// where the body will be by the time it lands. Walking, the steps go on the
// sim's own footwork pulse (GAIT), so a step's push-off is the surge in its
// speed; anything else (stopping, being backed off, a lunge) steps as needed.
// Positions are world units along the way the opponent faces (forward +).

import { GAIT, type EnemyKind } from '@/sim/tuning';

/**
 * How a type walks in. Every type comes in like a person, walking, jogging or
 * running by its speed with the feet passing (the user's call, 2026-10-11: a
 * step-drag at that pace splays and bunches the legs), each in its own way;
 * stopping, it settles into its guard's stance.
 */
export interface Gait {
  /** Its steps against a person's at the pace (1: as long; the boxer's are short). */
  stride: number;
  /** The longest step (world units). */
  longest: number;
  /** How high a stepping foot lifts walking at full stride, and the body rises with a step
   *  (body units). */
  lift: number;
  bounce: number;
  /** How far it straightens up out of its guard's crouch walking (1: the standing knee bent
   *  only as a person's is walking, the hips at STRAIGHT (renderer.ts) of the legs' length),
   *  and out of its guard's lean (1: upright). */
  upright: number;
  torso: number;
  /** Heels up walking (radians): on the balls of the feet. */
  heel: number;
  /** The upper body swaying on with each step, the hands swinging against the legs and
   *  the lean into a run (body units). */
  sway: number;
  swing: number;
  lean: number;
}

/**
 * Each type by its trade: the brawler walks up squaring his shoulders, the
 * bruiser lumbers, rolling with each step, the feinter goes light and loose,
 * the boxer comes in tucked behind his gloves with short quick steps, the
 * kickboxer springs tall on the balls of his feet, the wrestler hunches low.
 */
export const GAITS: Record<EnemyKind, Gait> = {
  basic: {
    stride: 0.85,
    longest: 0.85,
    lift: 0.08,
    bounce: 0.012,
    upright: 0.9,
    torso: 0.6,
    heel: 0,
    sway: 0.02,
    swing: 0.035,
    lean: 0.05,
  },
  heavy: {
    stride: 0.8,
    longest: 0.85,
    lift: 0.07,
    bounce: 0.008,
    upright: 0.75,
    torso: 0.4,
    heel: 0,
    sway: 0.035,
    swing: 0.025,
    lean: 0.04,
  },
  feinter: {
    stride: 0.85,
    longest: 0.8,
    lift: 0.08,
    bounce: 0.02,
    upright: 0.9,
    torso: 0,
    heel: 0.35,
    sway: 0.015,
    swing: 0.05,
    lean: 0.05,
  },
  blocker: {
    stride: 0.7,
    longest: 0.6,
    lift: 0.07,
    bounce: 0.012,
    upright: 0.6,
    torso: 0.15,
    heel: 0.2,
    sway: 0.01,
    swing: 0.012,
    lean: 0.06,
  },
  kicker: {
    stride: 0.9,
    longest: 0.9,
    lift: 0.1,
    bounce: 0.03,
    upright: 1,
    torso: 0.6,
    heel: 0.4,
    sway: 0.01,
    swing: 0.025,
    lean: 0.04,
  },
  grappler: {
    stride: 0.75,
    longest: 0.7,
    lift: 0.06,
    bounce: 0.006,
    upright: 0.45,
    torso: 0,
    heel: 0,
    sway: 0.02,
    swing: 0.02,
    lean: 0.04,
  },
};
/** Bosses walk in calm and upright, whatever act they're in. */
const BOSS: Gait = {
  stride: 0.85,
  longest: 0.85,
  lift: 0.08,
  bounce: 0.01,
  upright: 0.8,
  torso: 0.7,
  heel: 0,
  sway: 0.015,
  swing: 0.02,
  lean: 0.04,
};

/** How `kind` walks (a boss, as bosses do). */
export function gaitOf(kind: EnemyKind, boss: boolean): Gait {
  return boss ? BOSS : GAITS[kind];
}

/** No more steps a second than a sprinter's. */
const MAX_CADENCE = 4.4;

/** A person runs from this many leg lengths a second (some 2 m/s). */
const RUN_FROM = 2.3;

/** How a type moves at a speed: its gait, how often it steps, walking or running. */
export interface Rhythm {
  gait: Gait;
  /** Steps a second. */
  rate: number;
  running: boolean;
  /** The sim's pulse at its fastest, over its average speed, and pulses a second. */
  surge: number;
  pulse: number;
}

/**
 * How a type moves in at `speed` (world units/s), its legs `legs` long: its
 * gait, stepping as long as a person would at the pace (in leg lengths: the
 * faster, the longer, as walking; running from RUN_FROM, longer still) by its
 * own stride, no longer than its longest, and no longer than lets the body go
 * no further past a planted foot than its leg reaches (`reach`, world units).
 */
export function rhythmFor(
  kind: EnemyKind,
  boss: boolean,
  speed: number,
  reach = Infinity,
  legs = 0.82,
): Rhythm {
  const g = GAIT[kind];
  const pulse = g.stomp ? 2 * g.hz : g.hz;
  const surge = g.stomp ? 1 - g.depth + (g.depth * Math.PI) / 2 : 1 + g.depth;
  const gait = gaitOf(kind, boss);
  const x = speed / legs;
  const running = x > RUN_FROM;
  let step = Math.min(
    gait.longest,
    legs * gait.stride * (running ? 0.6 + 0.15 * x : 0.45 + 0.27 * x),
  );
  const fits = (): boolean => {
    const { stand } = stride(running, step / speed);
    return speed * fastest(surge, pulse, stand) * stand <= reach;
  };
  while (!fits() && (speed / step) * 1.05 <= MAX_CADENCE) step /= 1.05;
  return { gait, rate: speed / step, running, surge, pulse };
}

/**
 * How far ahead of walking at its average speed the sim's footwork pulse has
 * taken a `kind` (in seconds of that speed) by its clock `c` (sim.ts: the
 * walk's time plus its phase): ∫(gait − 1). Taken out, it walks at an even
 * pace, as a person does, barely surging with each step.
 */
export function surgeAt(kind: EnemyKind, c: number): number {
  const g = GAIT[kind];
  const w = 2 * Math.PI * g.hz;
  if (!g.stomp) return (g.depth * (1 - Math.cos(w * c))) / w;
  // The stomp's |sin|: each half wave adds 2/w
  const th = w * c;
  const n = Math.floor(th / Math.PI);
  return g.depth * ((Math.PI / 2) * ((2 * n + 1 - Math.cos(th - n * Math.PI)) / w) - c);
}

/**
 * How long a foot is in the air and then stands (seconds), a step `P` long:
 * walking, 0.85 of it and 1.15 (both feet down a while); running, 1.3 (both
 * off the floor a moment) and 0.7.
 */
export function stride(running: boolean, P: number): { dur: number; stand: number } {
  const dur = Math.min((running ? 1.3 : 0.85) * P, 0.5);
  return { dur, stand: 2 * P - dur };
}

/**
 * How much faster than its average the body goes past a foot standing
 * `stand` seconds, at the most: the sim's pulse at its peak (`surge`) for a
 * quick shuffle, about its average over a long stride.
 */
function fastest(surge: number, pulse: number, stand: number): number {
  const w = stand * pulse;
  const peak = w > 1e-6 ? Math.abs(Math.sin(Math.PI * w) / (Math.PI * w)) : 1;
  return 1 + (surge - 1) * peak;
}

/** A foot: planted at `at`, or on its way there from `from` (lifted `lift`, from `y0` up) till `t0 + dur`. */
interface Foot {
  at: number;
  from: number;
  y0: number;
  t0: number;
  dur: number;
  lift: number;
  /** Stepping to its spot, how far past it to land (aimed afresh all the way, so it lands
   *  under the body however that moves meanwhile); null: a step to a set place. */
  home: number | null;
  /** How long it's then to stand (seconds), the body going on past it: it lands where its
   *  leg reaches it all that time. */
  stand?: number;
}

/** What drives the feet each frame. */
export interface Drive {
  /** The body (world units forward). */
  u: number;
  /** Where each foot stands under the body as the pose has it: lead, rear. */
  spots: readonly [number, number];
  /** Where the pose itself has each foot, if not at its spot (walking, the feet
   *  pass under the body): a free one is set down from there. */
  own?: readonly [number, number];
  /** Walking: the footwork beat (steps go on its whole numbers); else null. */
  beat: number | null;
  /** Seconds a beat, and whether it's running. */
  period: number;
  running: boolean;
  gait: Gait;
  scale: number;
  /** Feet the pose moves itself (off the floor in a kick, say): they're left to it. */
  free: readonly [boolean, boolean];
  /** How high the pose has each foot (world units): a free one comes down from there. */
  heights: readonly [number, number];
  /** How far behind and ahead of the body each foot may land (world units): its leg's reach. */
  reach: readonly [readonly [number, number], readonly [number, number]];
  /** Walking, how fast on average (world units/s, forward +): where its steps are bound. */
  pace?: number;
  /** …and how much faster than that at its fastest, and how often it is (the sim's pulse). */
  surge?: number;
  pulse?: number;
}

/** Seconds a foot the pose takes over eases from where it stood, or settles from the pose's. */
const HANDOVER = 0.1;

/** How far ahead (seconds) to see where a foot stepping now is needed, and how fast it reacts. */
const SEE = 0.12;
/** A foot further than this (world units) from its spot steps there, standing or shoved. */
const ASTRAY = 0.07;
/** One much further steps even while the other is in the air (a scramble). */
const SCRAMBLE = 0.3;
/** A foot this far (world units) past where steps land steps however the walk goes. */
const STRETCH = 0.06;
/** How fast a stepping foot's aim may move (world units/s). */
const HOMING = 6;
/** How fast the hips may rise (body units/s), easing up over this long (seconds). */
const HIP_RISE = 1.5;
const HIP_EASE = 0.06;
/** Seconds the walking style takes to fade in or out. */
const STYLE_EASE = 0.2;
/** Steps shorter than this aren't worth taking. */
const STILL = 0.025;
/** Seconds over which the body's speed is read. */
const V_LAG = 0.12;

const smooth = (s: number): number => {
  const c = Math.max(0, Math.min(1, s));
  return c * c * (3 - 2 * c);
};

/** One opponent's feet: 0 the lead (`fFoot`), 1 the rear (`bFoot`). */
export class Feet {
  readonly foot: [Foot, Foot];
  /** The last beat stepped on (null: not walking). */
  private beat: number | null = null;
  /** Which feet the pose moves itself (a kick). */
  private free: [boolean, boolean] = [false, false];
  /** Whether the pose moves foot `i` itself (a kick), or the footwork does. */
  posed(i: 0 | 1): boolean {
    return this.free[i];
  }
  /** A foot the pose took over: how far from the pose's it was then (world units), and when. */
  private took: ({ dx: number; dy: number; t: number; knee?: [number, number] } | null)[] = [
    null,
    null,
  ];
  /** Where each knee was last drawn, from the body (world units): lead, rear. */
  knees: [[number, number] | null, [number, number] | null] = [null, null];
  /** Steps taken walking, for tests. */
  steps = 0;
  /** The last step a pose cued itself (the renderer's), so it's taken once. */
  cue: string | null = null;
  /** How much it's walking, eased (`walking()`), and when that was. */
  private style = 0;
  private styleT = -Infinity;
  /** How far the hips were last moved up or down (body units), and when. */
  private hip: number | null = null;
  private hipT = -Infinity;
  /** The body: where, when, how fast (world units/s, forward +). */
  u: number;
  t: number;
  v = 0;

  constructor(u: number, spots: readonly [number, number], t: number, v = 0) {
    const plant = (at: number): Foot => ({
      at,
      from: at,
      y0: 0,
      t0: t,
      dur: 0,
      lift: 0,
      home: null,
    });
    this.foot = [plant(spots[0]), plant(spots[1])];
    this.u = u;
    this.t = t;
    this.v = v;
  }

  /**
   * Feet already walking at `t` (as `d` drives them, at `v` world units/s):
   * walked for a few steps before, so they come onto the mat mid-stride.
   */
  static walking(d: Drive, t: number, v: number): Feet {
    const span = 8 * d.period;
    const back = (s: number): Drive => ({
      ...d,
      u: d.u - v * s,
      spots: [d.spots[0] - v * s, d.spots[1] - v * s],
      beat: d.beat === null ? null : d.beat - s / d.period,
    });
    const first = back(span);
    const f = new Feet(first.u, first.spots, t - span, v);
    for (let s = span; s > 0; s -= 1 / 240) f.update(back(s), t - s);
    f.steps = 0;
    f.update(d, t);
    return f;
  }

  /** How far a foot the pose just took over still is from the pose's (world units). */
  loose(i: 0 | 1, t: number): [number, number] {
    const took = this.took[i];
    if (!took) return [0, 0];
    const k = 1 - smooth((t - took.t) / HANDOVER);
    return [took.dx * k, took.dy * k];
  }

  /**
   * How far the knee of a leg the pose just took over still is from the
   * pose's (world units), `knee` where the pose has it now (from the body):
   * it eases in from where it was last drawn, as the foot does.
   */
  looseKnee(i: 0 | 1, t: number, knee: readonly [number, number]): [number, number] {
    const took = this.took[i];
    const was = this.knees[i];
    if (!took || !was) return [0, 0];
    took.knee ??= [was[0] - knee[0], was[1] - knee[1]];
    const k = 1 - smooth((t - took.t) / HANDOVER);
    return [took.knee[0] * k, took.knee[1] * k];
  }

  /** Where foot `i` is at `t`: along the floor and how high. */
  where(i: 0 | 1, t: number): [number, number] {
    const f = this.foot[i];
    const k = f.dur > 0 ? Math.min(1, (t - f.t0) / f.dur) : 1;
    const s = smooth(k);
    // Lifted highest a third of the way (the knee coming through), then
    // reaching out low to land
    return [f.from + (f.at - f.from) * s, f.y0 * (1 - s) + f.lift * 6.75 * k * (1 - k) ** 2];
  }

  swinging(i: 0 | 1, t: number): boolean {
    return t < this.foot[i].t0 + this.foot[i].dur;
  }

  /** How far through a step the body is (0–1, 0 with both feet down): it rises with it. */
  rise(t: number): number {
    let r = 0;
    for (const i of [0, 1] as const) {
      const f = this.foot[i];
      if (!this.free[i] && this.swinging(i, t))
        r = Math.max(r, Math.sin((Math.PI * (t - f.t0)) / f.dur));
    }
    return r;
  }

  /** How far through its step foot `i` is (0–1), or null standing. */
  through(i: 0 | 1, t: number): number | null {
    const f = this.foot[i];
    return !this.free[i] && this.swinging(i, t) ? (t - f.t0) / f.dur : null;
  }

  /**
   * How much it's walking (0–1, eased toward `to` over `EASE`): the walking
   * style fades in and out as it sets off, stops or squares up.
   */
  walking(to: number, t: number): number {
    const dt = Math.max(0, t - this.styleT);
    this.style += (to - this.style) * Math.min(1, dt / STYLE_EASE);
    this.styleT = t;
    return this.style;
  }

  /**
   * The hips moved `dy` (body units) as the legs have them, but rising no
   * faster than a body springs up: off both feet a moment, running, they don't
   * jump to wherever they'd be free to go. They come down at once.
   */
  hips(dy: number, t: number): number {
    if (this.hip === null || t < this.hipT) this.hip = dy;
    else if (t > this.hipT) {
      // (easing up, so a rise has no corner where it stops)
      const up = Math.max(0, dy - this.hip) * (1 - Math.exp(-(t - this.hipT) / HIP_EASE));
      this.hip = Math.min(dy, this.hip + Math.min(up, HIP_RISE * (t - this.hipT)));
    }
    this.hipT = t;
    return Math.min(dy, this.hip);
  }

  /** Where foot `i` is stepping to and how far through it is (0–1), or null standing. */
  landing(i: 0 | 1, t: number): { at: number; k: number } | null {
    const k = this.through(i, t);
    return k === null ? null : { at: this.foot[i].at, k };
  }

  /** Which foot is in the air and how far through its step, for the arms' swing. */
  stepping(t: number): { i: 0 | 1; k: number } | null {
    for (const i of [0, 1] as const) {
      const f = this.foot[i];
      if (!this.free[i] && this.swinging(i, t)) return { i, k: (t - f.t0) / f.dur };
    }
    return null;
  }

  /** Foot `i` steps to `to` (world units), taking `dur` seconds, lifted `lift` (`home`: see Foot). */
  step(
    i: 0 | 1,
    to: number,
    dur: number,
    lift: number,
    t: number,
    home: number | null = null,
    stand = 0,
  ): void {
    const [x, y] = this.where(i, t);
    this.foot[i] = { at: to, from: x, y0: y, t0: t, dur, lift, home, stand };
  }

  /** Foot `i` dragged to `at` along the floor: shoved further than a leg reaches. */
  drag(i: 0 | 1, at: number, t: number): void {
    const f = this.foot[i];
    const by = at - this.where(i, t)[0];
    f.from += by;
    f.at += by;
  }

  /** The feet where they're drawn (held still in a pose): planted there, an airborne one coming down. */
  hold(u: number, feet: readonly [[number, number], [number, number]], t: number): void {
    for (const i of [0, 1] as const) {
      const [x, y] = feet[i];
      this.foot[i] = {
        at: x,
        from: x,
        y0: y,
        t0: t,
        dur: y > 0.003 ? 0.08 : 0,
        lift: 0,
        home: null,
      };
    }
    this.v = t > this.t ? (u - this.u) / (t - this.t) : this.v;
    this.u = u;
    this.t = t;
    this.beat = null;
  }

  update(drive: Drive, t: number): void {
    const dt = t - this.t;
    // Nothing moves while the sim doesn't (hit-stop, pause)
    if (dt <= 0) return;
    this.v += ((drive.u - this.u) / dt - this.v) * Math.min(1, dt / V_LAG);
    this.u = drive.u;
    this.t = t;
    // Walking, but shoved back (by the one ahead in line): it steps back as it must
    let d = drive;
    if (d.beat !== null && this.v < -0.2) {
      d = { ...d, beat: null };
      delete (d as { pace?: number }).pace;
    }
    // Feet the pose moves itself go with it, easing in from where they
    // stood, and settle where it sets them down
    const own = d.own ?? d.spots;
    for (const i of [0, 1] as const) {
      const [x, y] = this.where(i, t);
      if (d.free[i] && !this.free[i]) this.took[i] = { dx: x - own[i], dy: y - d.heights[i], t };
      if (d.free[i]) {
        this.foot[i] = {
          at: own[i],
          from: own[i],
          y0: 0,
          t0: t,
          dur: 0,
          lift: 0,
          home: null,
        };
      } else if (this.free[i]) {
        // Set down: from where the pose has it, onto its spot under the body
        this.foot[i] = {
          at: this.within(i, d, d.spots[i], HANDOVER),
          from: own[i],
          y0: d.heights[i],
          t0: t,
          dur: HANDOVER,
          lift: 0,
          home: 0,
        };
      }
      this.free[i] = d.free[i];
      // A foot stepping to its spot keeps aiming where that will be as it lands
      const f = this.foot[i];
      const left = f.t0 + f.dur - t;
      if (f.home !== null && left > 0) {
        // (no faster than a body goes: its spot changing, a walk becoming a
        // windup, say, it doesn't jump after it)
        const want = this.within(i, d, d.spots[i] + f.home + this.v * left, left, f.stand);
        f.at += Math.max(-HOMING * dt, Math.min(HOMING * dt, want - f.at));
      }
    }
    if (d.beat === null) this.beat = null;
    else {
      const k = Math.floor(d.beat);
      // Starting to walk (or again), in step with the pulse from its next beat
      if (this.beat === null || k < this.beat) this.beat = k;
      else if (k > this.beat) {
        this.beat = k;
        this.onBeat(d, t);
      }
    }
    this.react(d, t);
  }

  /** A walking step: the foot left behind steps through. */
  private onBeat(d: Drive, t: number): void {
    // How far behind its spot each foot is, the way the body goes (the feet
    // take turns that way, and fall back into them after a stumble)
    const behind = (i: 0 | 1): number => Math.sign(this.v || 1) * (d.spots[i] - this.foot[i].at);
    const { dur } = stride(d.running, d.period);
    const order: (0 | 1)[] = behind(0) >= behind(1) ? [0, 1] : [1, 0];
    const i = order.find((j) => !this.free[j] && !this.swinging(j, t));
    if (i !== undefined && this.walkStep(i, d, t, dur, 2 * d.period)) this.steps++;
  }

  /**
   * Foot `i` steps to its spot as it will be when it lands, and on by half the
   * time it'll then stand, so it's under the body midway through: `cycle` is
   * the time from one of its steps to its next.
   */
  private walkStep(i: 0 | 1, d: Drive, t: number, dur: number, cycle: number): boolean {
    if (this.free[i] || this.swinging(i, t)) return false;
    const stand = Math.max(0, cycle - dur);
    const v = d.pace ?? this.v;
    // Walking, it lands less than half a step ahead and stays down till it's
    // left further behind, rolling onto its ball (as far back as a flat foot
    // reaches forward: the hips needn't drop as it lands); running, it lands
    // under the body
    const past = v * stand * (d.running ? 0.5 : 0.35);
    const to = this.within(i, d, d.spots[i] + past + v * dur, dur, stand);
    if (Math.abs(to - this.foot[i].at) < STILL) return false;
    // (lifted the higher the longer the step, and running higher)
    const lift =
      d.gait.lift *
      d.scale *
      (d.running ? 1.6 : 1) *
      Math.max(0.3, Math.min(1, Math.abs(to - this.foot[i].at) / (0.6 * d.scale)));
    this.step(i, to, dur, lift, t, past, stand);
    return true;
  }

  /**
   * `to`, but where foot `i`'s leg reaches it from the body as it lands in
   * `dur`, and still as the body goes on past it while it stands `stand`
   * after.
   */
  private within(i: 0 | 1, d: Drive, to: number, dur: number, stand = 0): number {
    const body = d.u + this.v * dur;
    const [lo, hi] = d.reach[i];
    // (walking, as far as it goes at its fastest over that time)
    const pace =
      d.pace === undefined ? this.v : d.pace * fastest(d.surge ?? 1, d.pulse ?? 1, stand);
    const drift = pace * stand;
    const min = body + lo + Math.max(0, drift);
    const max = body + hi + Math.min(0, drift);
    return min <= max ? Math.max(min, Math.min(max, to)) : (min + max) / 2;
  }

  /** A foot left too far from its spot steps there (walking, only one left far behind). */
  private react(d: Drive, t: number): void {
    // Walking, only one left further behind than the gait ever leaves a foot
    // (a step's drift: one left over from before it set off, say)
    const walking = d.beat !== null;
    const off = (i: 0 | 1): number => d.spots[i] + (walking ? 0 : this.v * SEE) - this.foot[i].at;
    const astray = walking ? ASTRAY + Math.abs(this.v) * d.period : ASTRAY;
    // …or any about to be out of its leg's reach (walking, a little past
    // where its steps land: they reach that just before their beat)
    const margin = walking ? STRETCH : 0;
    const stretched = (i: 0 | 1): boolean => {
      const rel = this.foot[i].at - d.u;
      return rel < d.reach[i][0] - margin || rel > d.reach[i][1] + margin;
    };
    const order: (0 | 1)[] =
      // Backing off the rear foot goes first, coming on the lead; standing, the one further out
      Math.abs(this.v) > 0.2
        ? this.v < 0
          ? [1, 0]
          : [0, 1]
        : Math.abs(off(0)) >= Math.abs(off(1))
          ? [0, 1]
          : [1, 0];
    for (const i of order) {
      if (this.free[i] || this.swinging(i, t)) continue;
      const other = (1 - i) as 0 | 1;
      const e = off(i);
      const limit =
        this.swinging(other, t) && !this.free[other] ? Math.max(SCRAMBLE, astray) : astray;
      if (Math.abs(e) < limit && !stretched(i)) continue;
      if (walking) {
        // A step of the walk's own, so it falls back into its rhythm
        const { dur } = stride(d.running, d.period);
        if (this.walkStep(i, d, t, dur, 2 * d.period)) return;
      }
      const dur = Math.min(0.2, 0.09 + Math.abs(e) * 0.12);
      const lift = Math.min(d.gait.lift * d.scale, 0.02 + Math.abs(e) * 0.12);
      this.step(i, this.within(i, d, d.spots[i] + this.v * dur, dur), dur, lift, t, 0);
      return;
    }
  }
}
